import { useAuthStore } from "@/states";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useParams } from "react-router";
import { useEffect, useState } from "react";

export type WhatsAppStatus = { enabled: boolean; configured: boolean };
export type WhatsAppAccount = { id: string; displayName: string; createdAt: string; state?: string };
export type WhatsAppConnection = { state: string; qrCode?: string | null; pairingCode?: string | null };
export type WhatsAppChat = {
  id: string;
  name: string;
  phone: string | null;
  unreadCount: number;
  lastMessage: string;
  lastMessageAt: string | null;
  isGroup: boolean;
};
export type WhatsAppMessage = {
  id: string;
  chatId: string;
  fromMe: boolean;
  senderName: string | null;
  text: string;
  messageType: string;
  timestamp: string | null;
  status: string | null;
  media?: { mimeType?: string; fileName?: string; size?: number };
};

/** Matches the server's limit for attachments decrypted through Evolution. */
export const WHATSAPP_MEDIA_MAX_BYTES = 16 * 1024 * 1024;
export type WhatsAppMessagePage = {
  data: WhatsAppMessage[];
  pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean };
};

const useWhatsAppQueryScope = () => {
  const user = useAuthStore((state) => state.user);
  const { tenantId } = useParams();
  return ["whatsapp", user?.role === "super_admin" && tenantId ? tenantId : user?.tenantId ?? user?.tenant?.id, user?.id] as const;
};

/** Receives authenticated Redis-backed hints; reconnects and reconciles missed events without exposing provider keys. */
export const useWhatsAppLiveUpdates = (accountId?: string): boolean => {
  const scope = useWhatsAppQueryScope();
  const [tenantId, userId] = [scope[1], scope[2]];
  const token = useAuthStore((state) => state.token);
  const queryClient = useQueryClient();
  const [liveAccount, setLiveAccount] = useState<string>();
  useEffect(() => {
    if (!accountId || !token) return;
    const controller = new AbortController();
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const pending = new Set<string>();
    const invalidate = (event: string) => {
      pending.add(event);
      if (debounce) return;
      debounce = setTimeout(() => {
        debounce = undefined;
        const all = pending.has("ready");
        const base = ["whatsapp", tenantId, userId];
        if (all || pending.has("chats") || pending.has("messages")) {
          void queryClient.invalidateQueries({ queryKey: [...base, "account", accountId, "chats"] });
        }
        if (all || pending.has("messages")) {
          void queryClient.invalidateQueries({ queryKey: [...base, "account", accountId, "messages"] });
        }
        if (all || pending.has("state")) {
          void queryClient.invalidateQueries({ queryKey: [...base, "account", accountId, "state"] });
          void queryClient.invalidateQueries({ queryKey: [...base, "accounts"] });
        }
        pending.clear();
      }, 150);
    };
    const connect = async () => {
      while (!controller.signal.aborted) {
        const connection = new AbortController();
        const abort = () => connection.abort();
        controller.signal.addEventListener("abort", abort, { once: true });
        const timeout = setTimeout(abort, 45_000);
        let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
        try {
          // The fetch adapter streams while retaining axios's JWT refresh and admin tenant interceptors.
          const { data } = await axios.get<ReadableStream<Uint8Array>>(`/api/whatsapp/accounts/${accountId}/events`, {
            adapter: "fetch", responseType: "stream", signal: connection.signal,
            headers: { Accept: "text/event-stream" },
          });
          reader = data.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!controller.signal.aborted) {
            const chunk = await reader.read();
            if (chunk.done || controller.signal.aborted) break;
            buffer += decoder.decode(chunk.value, { stream: true });
            const frames = buffer.split("\n\n");
            buffer = frames.pop() || "";
            if (buffer.length > 4096) throw new Error("Invalid WhatsApp event stream");
            for (const frame of frames) {
              const event = frame.split("\n").find((line) => line.startsWith("event: "))?.slice(7);
              if (event && ["ready", "chats", "messages", "state"].includes(event)) {
                if (event === "ready") setLiveAccount(accountId);
                invalidate(event);
              }
            }
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.warn("WhatsApp live updates interrupted; polling remains active");
            if (axios.isAxiosError(error) && [401, 403, 404].includes(error.response?.status ?? 0)) break;
          }
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener("abort", abort);
          connection.abort();
          reader?.releaseLock();
          if (!controller.signal.aborted) setLiveAccount(undefined);
        }
        if (controller.signal.aborted) break;
        await new Promise<void>((resolve) => {
          const finish = () => { clearTimeout(timer); controller.signal.removeEventListener("abort", finish); resolve(); };
          const timer = setTimeout(finish, 5_000);
          controller.signal.addEventListener("abort", finish, { once: true });
        });
      }
    };
    void connect();
    return () => {
      controller.abort();
      if (debounce) clearTimeout(debounce);
      setLiveAccount(undefined);
    };
  }, [accountId, token, tenantId, userId, queryClient]);
  return Boolean(accountId && liveAccount === accountId);
};

/** Fetches server-side WhatsApp availability for the authenticated CRM user. */
export const useWhatsAppStatusQuery = (enabled = true) => {
  const scope = useWhatsAppQueryScope();
  const token = useAuthStore((state) => state.token);
  return useQuery({
    queryKey: [...scope, "status"],
    queryFn: async ({ signal }): Promise<WhatsAppStatus> =>
      (await axios.get<WhatsAppStatus>("/api/whatsapp/status", { signal })).data,
    enabled: enabled && Boolean(token),
    staleTime: 60_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
};

/** Lists accessible WhatsApp accounts and refreshes account assignments. */
export const useWhatsAppAccountsQuery = (enabled: boolean, view: "all" | "connected" | "management" = "all") => {
  const scope = useWhatsAppQueryScope();
  return useQuery({
    queryKey: [...scope, "accounts", view],
    queryFn: async ({ signal }): Promise<{ data: WhatsAppAccount[] }> =>
      (await axios.get<{ data: WhatsAppAccount[] }>("/api/whatsapp/accounts", {
        signal, params: view === "connected" ? { connected: true } : view === "management" ? { includeState: true } : undefined,
      })).data,
    enabled,
    refetchInterval: 30_000,
    retry: false,
  });
};

/** Creates a WhatsApp account without exposing Evolution credentials to the browser. */
export const useCreateWhatsAppAccountMutation = () => {
  const scope = useWhatsAppQueryScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (displayName: string): Promise<{ data: WhatsAppAccount }> =>
      (await axios.post<{ data: WhatsAppAccount }>("/api/whatsapp/accounts", { displayName })).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...scope, "accounts"] });
    },
  });
};

/** Obtains a QR code for linking or reconnecting a tenant-owned account. */
export const useConnectWhatsAppAccountMutation = () => {
  const scope = useWhatsAppQueryScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string): Promise<{ data: WhatsAppConnection }> =>
      (await axios.post<{ data: WhatsAppConnection }>(`/api/whatsapp/accounts/${accountId}/connect`)).data,
    onSuccess: async (result, accountId) => {
      queryClient.setQueryData([...scope, "account", accountId, "state"], result);
      await queryClient.invalidateQueries({ queryKey: [...scope, "accounts"] });
    },
  });
};

/** Disconnects an account and refreshes management status and connected inbox choices. */
export const useDisconnectWhatsAppAccountMutation = () => {
  const scope = useWhatsAppQueryScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string): Promise<{ data: WhatsAppConnection }> =>
      (await axios.post<{ data: WhatsAppConnection }>(`/api/whatsapp/accounts/${accountId}/disconnect`)).data,
    onSuccess: async (result, accountId) => {
      await queryClient.cancelQueries({ queryKey: [...scope, "account", accountId] });
      queryClient.removeQueries({ queryKey: [...scope, "account", accountId] });
      queryClient.setQueryData([...scope, "account", accountId, "state"], result);
      await queryClient.invalidateQueries({ queryKey: [...scope, "accounts"] });
    },
  });
};

/** Deletes an account and removes its cached conversations and stale assignment choices. */
export const useDeleteWhatsAppAccountMutation = () => {
  const scope = useWhatsAppQueryScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string): Promise<{ message: string }> =>
      (await axios.delete<{ message: string }>(`/api/whatsapp/accounts/${accountId}`)).data,
    onSuccess: async (_result, accountId) => {
      await queryClient.cancelQueries({ queryKey: [...scope, "account", accountId] });
      queryClient.removeQueries({ queryKey: [...scope, "account", accountId] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...scope, "accounts"] }),
        queryClient.invalidateQueries({ queryKey: [...scope, "status"] }),
        queryClient.invalidateQueries({ queryKey: ["user-whatsapp-accounts", scope[1]] }),
        queryClient.invalidateQueries({ queryKey: ["user-whatsapp-access", scope[1]] }),
      ]);
    },
  });
};

/** Polls account connection state, with faster updates while a QR code is visible. */
export const useWhatsAppAccountStateQuery = (accountId?: string, connecting = false, live = false) => {
  const scope = useWhatsAppQueryScope();
  return useQuery({
    queryKey: [...scope, "account", accountId, "state"],
    queryFn: async ({ signal }): Promise<{ data: WhatsAppConnection }> =>
      (await axios.get<{ data: WhatsAppConnection }>(`/api/whatsapp/accounts/${accountId}/state`, { signal })).data,
    enabled: Boolean(accountId),
    refetchInterval: connecting ? 3_000 : live ? 60_000 : 15_000,
    retry: false,
  });
};

/** Polls chat threads, including saved history for disconnected accounts. */
export const useWhatsAppChatsQuery = (accountId?: string, live = false) => {
  const scope = useWhatsAppQueryScope();
  return useQuery({
    queryKey: [...scope, "account", accountId, "chats"],
    queryFn: async ({ signal }): Promise<{ data: WhatsAppChat[] }> =>
      (await axios.get<{ data: WhatsAppChat[] }>(`/api/whatsapp/accounts/${accountId}/chats`, { signal })).data,
    enabled: Boolean(accountId),
    refetchInterval: live ? 60_000 : 15_000,
    retry: false,
  });
};

/** Loads older messages and periodically refreshes the selected conversation. */
export const useWhatsAppMessagesQuery = (accountId: string, chatId: string, live = false) => {
  const scope = useWhatsAppQueryScope();
  return useInfiniteQuery({
    queryKey: [...scope, "account", accountId, "messages", chatId],
    queryFn: async ({ pageParam, signal }): Promise<WhatsAppMessagePage> =>
      (await axios.get<WhatsAppMessagePage>(`/api/whatsapp/accounts/${accountId}/messages`, {
        params: { chatId, page: pageParam, limit: 50 },
        signal,
      })).data,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined,
    enabled: Boolean(accountId && chatId),
    refetchInterval: live ? 60_000 : 10_000,
    retry: false,
  });
};

const MAX_CONCURRENT_MEDIA_REQUESTS = 3;
let activeMediaRequests = 0;
const queuedMediaRequests: Array<() => void> = [];

/** Runs attachment downloads through a small queue so a thread full of images cannot flood the server and Evolution. */
const withMediaSlot = async <T,>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> => {
  if (activeMediaRequests >= MAX_CONCURRENT_MEDIA_REQUESTS) {
    await new Promise<void>((resolve, reject) => {
      const start = () => { signal?.removeEventListener("abort", cancel); resolve(); };
      const cancel = () => {
        const index = queuedMediaRequests.indexOf(start);
        if (index >= 0) queuedMediaRequests.splice(index, 1);
        reject(new DOMException("Aborted", "AbortError"));
      };
      if (signal?.aborted) return cancel();
      signal?.addEventListener("abort", cancel, { once: true });
      queuedMediaRequests.push(start);
    });
  }
  activeMediaRequests += 1;
  try {
    return await task();
  } finally {
    activeMediaRequests -= 1;
    queuedMediaRequests.shift()?.();
  }
};

/** Downloads one decrypted attachment from an authorized chat as a Blob. */
export const fetchWhatsAppMedia = async (accountId: string, chatId: string, messageId: string, signal?: AbortSignal): Promise<Blob> =>
  withMediaSlot(async () => (await axios.get<Blob>(`/api/whatsapp/accounts/${accountId}/messages/${encodeURIComponent(messageId)}/media`, {
    params: { chatId }, responseType: "blob", signal,
  })).data, signal);

/** Loads an attachment once per session; media never changes, so it is never refetched. */
export const useWhatsAppMediaQuery = (accountId: string, chatId: string, messageId: string, enabled: boolean) => {
  const scope = useWhatsAppQueryScope();
  return useQuery({
    queryKey: [...scope, "account", accountId, "media", chatId, messageId],
    queryFn: ({ signal }) => fetchWhatsAppMedia(accountId, chatId, messageId, signal),
    enabled,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    // The server caps concurrent downloads per user; retry briefly when it asks us to slow down.
    retry: (count, error) => axios.isAxiosError(error) && error.response?.status === 429 && count < 3,
    retryDelay: 2_000,
  });
};

/** Sends a reply and refreshes only the originating account and conversation. */
export const useSendWhatsAppMessageMutation = () => {
  const scope = useWhatsAppQueryScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { accountId: string; chatId: string; text: string }): Promise<{ data: WhatsAppMessage }> =>
      (await axios.post<{ data: WhatsAppMessage }>(`/api/whatsapp/accounts/${payload.accountId}/messages`, {
        chatId: payload.chatId,
        text: payload.text,
      })).data,
    onSuccess: async (_result, payload) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...scope, "account", payload.accountId, "chats"] }),
        queryClient.invalidateQueries({ queryKey: [...scope, "account", payload.accountId, "messages", payload.chatId] }),
      ]);
    },
  });
};
